import React from "react";
import { IconBolt, IconCheck, IconErrand, IconFood, IconGrocery, IconPin, IconReceipt } from "./Icons";
import { SHOW_PRICING } from "../siteConfig";
import "../styles/app-preview.css";

/**
 * A composed, product-accurate preview of the PABS app used in the hero.
 *
 * This is deliberately built from real UI primitives (a service picker, a live
 * order card, a fare breakdown) rather than a stock illustration, so the first
 * screen shows what the product actually does. Purely decorative to assistive
 * tech — every fact shown here is also stated in the surrounding copy.
 */
const AppPreview: React.FC = () => (
  <div className="apx" aria-hidden="true">
    <div className="apx__glow" />

    {/* Stage is exactly phone-width, so the floating chips below can be offset
       relative to the phone itself rather than the surrounding grid column. */}
    <div className="apx__stage">
      {/* ---- Phone -------------------------------------------------------- */}
      <div className="apx__phone">
        <div className="apx__screen">
          <div className="apx__status">
            <span>9:41</span>
            <span className="apx__status-icons">
              <i /> <i /> <i />
            </span>
          </div>

          <div className="apx__app">
            <div className="apx__greeting">
              <span className="apx__hi">Good evening</span>
              <span className="apx__where">
                <IconPin size={13} />
                Mahinog, Camiguin
              </span>
            </div>

            <div className="apx__services">
              <div className="apx__service apx__service--amber">
                <IconFood size={19} />
                <span>Foods</span>
              </div>
              <div className="apx__service apx__service--teal">
                <IconGrocery size={19} />
                <span>Pabili</span>
              </div>
              <div className="apx__service apx__service--violet is-active">
                <IconErrand size={19} />
                <span>Pasugo</span>
              </div>
            </div>

            <div className="apx__track">
              <div className="apx__track-head">
                <span className="apx__track-label">
                  <span className="pulse-dot" />
                  On the way
                </span>
                <span className="apx__eta">12 min</span>
              </div>

              <div className="apx__route">
                <div className="apx__stop">
                  <span className="apx__dot apx__dot--from" />
                  <div>
                    <b>Pickup</b>
                    <small>Poblacion market</small>
                  </div>
                </div>
                <div className="apx__line">
                  <span className="apx__line-fill" />
                </div>
                <div className="apx__stop">
                  <span className="apx__dot apx__dot--to" />
                  <div>
                    <b>Drop-off</b>
                    <small>Benoni, Mahinog</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Worked example on the published schedule: ₱50 Pasugo flag-down,
                first km free, ₱15 for each km after that (4 km → 3 chargeable). */}
            {SHOW_PRICING ? (
            <div className="apx__fare">
              <div className="apx__fare-row">
                <span>Flag-down</span>
                <b>₱50</b>
              </div>
              <div className="apx__fare-row">
                <span>3 km after first</span>
                <b>₱45</b>
              </div>
              <div className="apx__fare-row apx__fare-row--total">
                <span>Estimated total</span>
                <b>₱95</b>
              </div>
            </div>
            ) : (
            <div className="apx__fare">
              <div className="apx__fare-row">
                <span>Fare estimate</span>
                <b>Before you confirm</b>
              </div>
            </div>
            )}

            <div className="apx__cta">Book now</div>
          </div>
        </div>
      </div>

      {/* ---- Floating chips ----------------------------------------------- */}
      <div className="apx__float apx__float--rider">
        <span className="apx__avatar">
          <IconBolt size={16} />
        </span>
        <div>
          <b>Rider assigned</b>
          <small>Picked up your order</small>
        </div>
      </div>

      <div className="apx__float apx__float--paid">
        <span className="apx__tick">
          <IconCheck size={14} />
        </span>
        <div>
          <b>Cash or GCash</b>
          <small>You choose at checkout</small>
        </div>
      </div>

      <div className="apx__float apx__float--fare">
        <span className="apx__fare-chip">
          {SHOW_PRICING ? "₱95" : <IconReceipt size={16} />}
        </span>
        <div>
          <b>Fare shown upfront</b>
          <small>Before you confirm</small>
        </div>
      </div>
    </div>
  </div>
);

export default AppPreview;
